import { useEffect, useCallback, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { motion } from 'motion/react';
import { Link } from '@/shared/components/atoms/Link';
import Button from '@/shared/components/flip-ui-kit/Button';
import useScreenSize from '@/shared/hooks/useScreenSize';
import useWindowEventListener from '@/shared/hooks/useWindowEventListener';
import ChevronIcon from '@/shared/icons/flip-ui-kit/large/Chevron';
import WindowIcon from '@/shared/icons/flip-ui-kit/small/Window';

export type DropdownItem = {
  label: string;
  icon?: ReactNode;
  href: string;
  activePath?: string;
  action?: string;
  onClick?: () => void;
  comingSoon?: boolean;
  newBadge?: boolean;
};

export type DropdownCTA = {
  label: string;
  href: string;
};

export type DropdownProps = {
  title: ReactNode;
  items: DropdownItem[];
  cta?: DropdownCTA;
  titleClassAdditional?: string;
};

const animate = {
  enter: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.15,
    },
    display: 'block',
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
    transitionEnd: {
      display: 'none',
    },
  },
};

const ComingSoonPill = () => (
  <div className="inline-flex h-6 items-center gap-x-0.5 rounded-[30px] border border-cf-green-1/20 bg-cf-green-4 px-2 py-1 text-12 text-cf-green-1">
    Soon
  </div>
);

const DropdownItemContent = ({ item }: { item: DropdownItem }) => (
  <div className="group flex cursor-pointer items-center gap-x-2 text-13 md:p-3 md:pr-4">
    {item.icon && (
      <div className="hidden text-cf-light-2 transition-colors duration-150 group-hover:text-cf-white md:block">
        {item.icon}
      </div>
    )}
    <span
      className={classNames(
        item.comingSoon ? 'text-cf-light-1' : 'text-cf-light-4',
        'font-aeonikMedium',
      )}
    >
      {item.label}
    </span>
    {item.comingSoon && <ComingSoonPill />}
    {item.newBadge && <span className="text-cf-green-3">New!</span>}
  </div>
);

const NavbarDropdown = ({ title, items, cta, titleClassAdditional }: DropdownProps) => {
  const { isMobile, isTablet } = useScreenSize();
  const { pathname } = useRouter();
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    animate.enter.transition.duration = isMobile ? 0 : 0.15;
    animate.exit.transition.duration = isMobile ? 0 : 0.15;
  }, [isMobile]);
  const outerDiv = useRef<HTMLDivElement>(null);
  const innerDiv = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      // on mobile/tablets we want to close the menu if the user clicks outside
      if (isTablet && outerDiv.current && !outerDiv.current.contains(e.target as Node)) {
        setIsActive(false);
      }
    },
    [isTablet],
  );

  // on tablet, close dropdown when clicking outside
  useWindowEventListener('click', handleClickOutside);

  return (
    <motion.div
      ref={outerDiv}
      onMouseLeave={isTablet ? undefined : () => setIsActive(false)}
      onClick={(e) => {
        // on mobile/tablets we want to close the menu if the user clicks outside
        // or on the menu button again
        if (isTablet && !innerDiv.current?.contains(e.target as Node)) {
          setIsActive(Boolean(outerDiv.current?.contains(e.target as Node)));
        } else {
          setIsActive(!isActive);
        }
      }}
    >
      <motion.div
        ref={innerDiv}
        onMouseEnter={isTablet ? undefined : () => setIsActive(true)}
        className={classNames(
          'group flex cursor-default items-center justify-between lg:justify-normal',
          isActive && 'text-cf-white',
        )}
      >
        <span
          className={classNames(
            'inline-flex items-center font-aeonikMedium text-14 transition-colors duration-150 ease-out',
            titleClassAdditional,
            items.some((item) => pathname.startsWith(item.activePath ?? item.href)) &&
              '!text-cf-green-1 underline underline-offset-2',
          )}
        >
          {title}
          <ChevronIcon flip={!isActive} transition />
        </span>
      </motion.div>
      <motion.div
        initial="exit"
        animate={isActive ? 'enter' : 'exit'}
        variants={animate}
        className={classNames(
          'z-10 mb-2 mt-6 flex w-full min-w-[200px] flex-col space-y-6 rounded-lg text-16 text-cf-white shadow-grayPop2',
          'md:absolute md:m-0 md:w-max md:space-y-0 md:border md:border-cf-gray-4 md:bg-cf-gray-3-5 md:text-14',
        )}
        style={{
          backdropFilter: 'blur(15px)',
        }}
      >
        {items.map((item) => (
          <div
            key={item.label}
            className={classNames(
              !item.comingSoon && !isMobile && 'hover:bg-cf-gray-4',
              'first:rounded-t-lg last:rounded-b-lg',
            )}
          >
            {item.comingSoon ? (
              <DropdownItemContent item={item} />
            ) : (
              <Link
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                onClick={item.onClick}
                className="pointer-events-auto"
              >
                <DropdownItemContent item={item} />
              </Link>
            )}
          </div>
        ))}
        {cta && (
          <div className="md:px-4 md:pb-3 md:pt-2">
            <Link href={cta.href} target="_blank">
              <Button
                className="hidden w-full items-center gap-x-1 lg:flex"
                iconPos="right"
                icon={<WindowIcon />}
                size="small"
              >
                {cta.label}
              </Button>
              <div className="lg:hidden">
                <DropdownItemContent item={cta} />
              </div>
            </Link>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default NavbarDropdown;
